/**
 * Everything about one user, and every action an admin can take on them.
 *
 * Fetched fresh on open (`useUserDetail`, `enabled` on the id) rather than handed the
 * table's row as props — a sheet that shows yesterday's role is worse than one that
 * takes a moment to load, and .claude/rules/frontend.md says as much.
 */

import { Building2, KeyRound, LogOut, Shield, ShieldOff, Trash2 } from "lucide-react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import type { UserRow } from "@/hooks/use-users";
import { useRevokeSessions, useUserDetail } from "@/hooks/use-users";
import { formatSignIn, ProviderBadge, RoleSourceBadge } from "./user-presentation";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  isSelf: boolean;
  onChangeRole: (user: UserRow) => void;
  onManageAccounts: (user: UserRow) => void;
  onSetAccess: (user: UserRow) => void;
  onResetPassword: (user: UserRow) => void;
  onDelete: (user: UserRow) => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

export function UserDetailDialog({
  open,
  onOpenChange,
  userId,
  isSelf,
  onChangeRole,
  onManageAccounts,
  onSetAccess,
  onResetPassword,
  onDelete,
}: Props) {
  const { data, isLoading, isError, error, refetch } = useUserDetail(open ? userId : null);
  const revokeSessions = useRevokeSessions();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{data?.name ?? "User"}</DialogTitle>
          <DialogDescription>{data?.email ?? "Loading…"}</DialogDescription>
        </DialogHeader>

        {isError ? (
          <ErrorState
            message={error?.message ?? "Failed to load this user"}
            onRetry={() => refetch()}
          />
        ) : isLoading || !data ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Role">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{data.role}</Badge>
                  <RoleSourceBadge source={data.roleSource} />
                </div>
              </Field>
              <Field label="Access">
                <Badge variant={data.accessStatus === "active" ? "secondary" : "destructive"}>
                  {data.accessStatus === "active" ? "Active" : "Disabled"}
                </Badge>
              </Field>
              <Field label="Signs in with">
                <div className="flex flex-wrap gap-1">
                  {data.providerDetail.length === 0 ? (
                    <span className="text-muted-foreground">Password</span>
                  ) : (
                    data.providerDetail.map((p) => (
                      <ProviderBadge key={p.provider} provider={p.provider} />
                    ))
                  )}
                </div>
              </Field>
              <Field label="Last sign-in">{formatSignIn(data.lastSignInAt)}</Field>
              <Field label="Open sessions">{data.activeSessionCount}</Field>
              <Field label="Linked Resource">
                {data.resourceName ?? <span className="text-muted-foreground">None</span>}
              </Field>
            </div>

            {data.accessStatus === "disabled" && data.banReason && (
              <p className="rounded-md border border-border bg-muted/40 p-3 text-sm">
                Disabled: {data.banReason}
                {data.banExpires
                  ? ` (until ${new Date(data.banExpires).toLocaleDateString()})`
                  : ""}
              </p>
            )}

            <div className="space-y-2">
              <p className="text-sm font-medium">Accounts they look after</p>
              {data.ownedAccounts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {data.role === "ACCT"
                    ? "None yet — an Account Manager with no Accounts cannot open or plan for anything."
                    : "None."}
                </p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {data.ownedAccounts.map((account) => (
                    <Badge key={account.ownershipId} variant="outline">
                      {account.accountName}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {data.roleSource !== "manual" && data.mappedRole && (
              <p className="text-xs text-muted-foreground">
                Automatic mapping currently makes this person {data.mappedRole}.
              </p>
            )}

            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              <Button variant="outline" size="sm" onClick={() => onManageAccounts(data)}>
                <Building2 className="mr-2 h-4 w-4" />
                Accounts
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={isSelf}
                onClick={() => onChangeRole(data)}
              >
                <Shield className="mr-2 h-4 w-4" />
                Change role
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={isSelf}
                onClick={() => onSetAccess(data)}
              >
                <ShieldOff className="mr-2 h-4 w-4" />
                {data.accessStatus === "active" ? "Disable access" : "Restore access"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={isSelf || revokeSessions.isPending || data.activeSessionCount === 0}
                onClick={() => revokeSessions.mutate({ userId: data.id })}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out everywhere
              </Button>
              <Button variant="outline" size="sm" onClick={() => onResetPassword(data)}>
                <KeyRound className="mr-2 h-4 w-4" />
                Reset password
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive"
                disabled={isSelf}
                onClick={() => onDelete(data)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </div>

            {isSelf && (
              <p className="text-xs text-muted-foreground">
                You cannot change the role, access or existence of your own account.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
